import { createContext, useContext } from 'react'
import type { ReactNode } from 'react'

export interface CalendarDisplay {
  /**
   * Print each game's season (competition) on its card.
   *
   * On by the hosts that let games from more than one competition share a
   * calendar — a team schedule with "All Seasons" chosen. Off everywhere a
   * season is already picked, where naming it on every card would be noise.
   */
  showSeasonName: boolean
}

const DEFAULT: CalendarDisplay = { showSeasonName: false }

const CalendarDisplayContext = createContext<CalendarDisplay>(DEFAULT)

/**
 * Card-level display switches, held in context rather than threaded as props.
 *
 * The card sits three levels below the layouts (grid → day cell → card) and is
 * rendered from five places across month, week, day and agenda. Passing a
 * display flag down all of them would put a prop nobody in between reads on
 * every one of those components, and a host that forgot one would get a
 * calendar whose cards disagree with each other.
 *
 * Consumers with no provider get the defaults, so a host that renders one of
 * these layouts and knows nothing about seasons behaves exactly as before.
 */
export const CalendarDisplayProvider = ({
  showSeasonName = false,
  children,
}: {
  showSeasonName?: boolean
  children: ReactNode
}) => (
  <CalendarDisplayContext.Provider value={{ showSeasonName }}>
    {children}
  </CalendarDisplayContext.Provider>
)

export const useCalendarDisplay = (): CalendarDisplay =>
  useContext(CalendarDisplayContext)

export default CalendarDisplayContext
